import { biffCmd, installBanners, isBiffLoadRequested, loadBiff } from '@/app/common/biff/biffLoader.ts';
import { type BIFF_CONFIGURATIONS, POSITIONS } from './typesSearchPage.ts';

const AFS_OPTIONS = { number: 3 };

export const loadGPT = ({ configuration }: { configuration: BIFF_CONFIGURATIONS }): void => {
  const { GPT } = POSITIONS[configuration];
  if (GPT.length === 0) return;

  installBanners({ elements: GPT, provider: 'googleDFP' });
};

/**
 * AFS (AdSense for Search) needs the search query enriched with the destination before its slots are
 * installed. The query helpers go through gpt, which only exists once biff has loaded the configuration —
 * `initBiffSearchPage` calls this after that point.
 */
const loadAFS = ({
  configuration,
  destination,
}: {
  configuration: BIFF_CONFIGURATIONS;
  destination?: string;
}): void => {
  const { AFS } = POSITIONS[configuration];
  if (AFS.length === 0) return;

  biffCmd((biff) => {
    biff.Includer.getAsyncSearchEnginesQuery()
      .then((searchQuery) => {
        const words = [searchQuery, destination]
          .filter((part): part is string => !!part)
          .join(' ')
          .split(' ');
        biff.Includer.setSearchEnginesQuery([...new Set(words)].join(' '));
        biff.Includer.installBanners({ elements: AFS, provider: 'googleAFS', options: AFS_OPTIONS });
      })
      // Without this the rejection is unhandled and the AFS slots are silently left out.
      .catch((error) => {
        console.error('[biff] AFS query failed', { configuration, error });
      });
  });
};

type LoadConfigurationsProps = {
  configuration: BIFF_CONFIGURATIONS;
  destination?: string;
};
export const loadConfigurationsSearchPage = ({ configuration, destination }: LoadConfigurationsProps): void => {
  if (!POSITIONS[configuration]) return;

  loadGPT({ configuration });
  loadAFS({ configuration, destination });
};

type InitBiffSearchPageProps = {
  configuration: BIFF_CONFIGURATIONS;
  destination?: string;
  isLoggedIn: boolean;
  /** `window.platformData.length` when the page started, so only this page's visit event is awaited. */
  fromIndex?: number;
  /** Aborts the request when the page goes away, so a load still queued on consent cannot install its slots. */
  signal?: AbortSignal;
};

/**
 * Requests the search-page ad configuration and installs its slots once biff has defined them. The biff
 * script is booted page-wide by BiffBootstrap and gates on consent on its own.
 *
 * The slots are registered only after `load()` for this configuration has completed: registered earlier,
 * biff would run the registration against the previous page's slots (a client-side destinations → results
 * or results → detail navigation) and drop every id it does not know yet. The loader keeps one request per
 * configuration until biff loads a different one, so the effects calling this can run as often as they like:
 * the slots are installed once per load(), and a new search after a hotel detail visit loads them again.
 */
export const initBiffSearchPage = ({
  configuration,
  destination,
  isLoggedIn,
  fromIndex,
  signal,
}: InitBiffSearchPageProps): void => {
  if (isBiffLoadRequested(configuration)) return;

  loadBiff({ isLoggedIn, key: configuration, fromIndex, signal }).then((loaded) => {
    if (loaded && !signal?.aborted) loadConfigurationsSearchPage({ configuration, destination });
  });
};
